// Production widget is API-agnostic: the same snippet key may serve Classic or Messenger
// after Support flips "Turn on messaging". Classic identify/prefill has no Messenger
// equivalent here; JWT loginUser is deferred. Verify both keys in preview before prod toggle.
export const ZENDESK_SCRIPT_ID = "ze-snippet"
export const WIDGET_Z_INDEX = 30

const ZDASSETS_HOST = "zdassets.com"

export type ZendeskChannel = "webWidget" | "webWidget:on" | "webWidget:get" | "messenger" | "messenger:set"

export type ProductionWidgetUser = {sub: string; org: string}

export function ze(channel: ZendeskChannel, command: string, payload?: unknown): boolean {
	try {
		if (!window.zE) {
			return false
		}
		window.zE(channel, command, payload)
		return true
	} catch {
		return false
	}
}

function swallowZdassetsErrors(event: ErrorEvent) {
	if (typeof event.filename === "string" && event.filename.includes(ZDASSETS_HOST)) {
		event.preventDefault()
	}
}

const SDK_READY_POLL_INTERVAL_MS = 200
const SDK_READY_TIMEOUT_MS = 15000

const WIDGET_FRAME_SELECTOR = 'iframe#launcher, iframe#webWidget, iframe[title*="messag" i], iframe[title*="widget" i]'

// snippet.js returns immediately and queues commands; the product bundle it pulls in only runs them
// later, reporting unsupported ones itself. Until the widget has actually rendered, a capability
// check can't tell Classic from Messenger, so wait for a rendered frame before probing.
export function isZendeskSdkReady() {
	if (!window.zE) {
		return false
	}
	try {
		return Boolean(document.querySelector(WIDGET_FRAME_SELECTOR))
	} catch {
		return false
	}
}

function whenZendeskSdkReady(onReady: () => void) {
	const runOnce = () => {
		try {
			onReady()
		} catch {
			// Widget configure failed; keep the dashboard usable
		}
	}

	if (isZendeskSdkReady()) {
		runOnce()
		return () => undefined
	}

	const startedAt = Date.now()
	const timer = window.setInterval(() => {
		if (!isZendeskSdkReady() && Date.now() - startedAt < SDK_READY_TIMEOUT_MS) {
			return
		}
		window.clearInterval(timer)
		runOnce()
	}, SDK_READY_POLL_INTERVAL_MS)

	return () => window.clearInterval(timer)
}

export function loadZendeskSnippet({id, src, onReady}: {id: string; src: string; onReady: () => void}) {
	if (document.getElementById(id)) {
		return whenZendeskSdkReady(onReady)
	}

	window.addEventListener("error", swallowZdassetsErrors)

	let cancelReadyPolling = () => undefined as void

	const removeGuard = () => {
		window.removeEventListener("error", swallowZdassetsErrors)
	}

	const script = document.createElement("script")
	script.id = id
	script.src = src
	script.async = true
	script.onload = () => {
		removeGuard()
		cancelReadyPolling = whenZendeskSdkReady(onReady)
	}
	script.onerror = () => {
		removeGuard()
		script.remove()
		console.warn("Zendesk widget failed to load")
	}
	document.body.appendChild(script)

	return () => cancelReadyPolling()
}

export function configureProductionWidget(userTokenData: ProductionWidgetUser, isSuperUser: boolean) {
	if (!window.zE) {
		return
	}

	if (ze("webWidget", "show")) {
		ze("webWidget", "updateSettings", {
			webWidget: {
				contactForm: {
					suppress: false,
					selectTicketForm: {
						"*": "What can we help you with?",
					},
				},
				zIndex: WIDGET_Z_INDEX,
			},
		})

		ze("webWidget", "identify", {
			name: "",
			email: userTokenData.sub,
			organization: userTokenData.org,
		})

		if (!isSuperUser && userTokenData.sub) {
			ze("webWidget", "prefill", {
				email: {
					value: userTokenData.sub,
					readOnly: true,
				},
			})
		}
		return
	}

	// Messenger does not support Classic identify/prefill. JWT loginUser is a follow-up.
	ze("messenger", "show")
	ze("messenger:set", "zIndex", WIDGET_Z_INDEX)
}

export function logoutZendesk() {
	if (!window.zE) {
		return
	}

	ze("webWidget", "logout")
	ze("webWidget", "updateSettings", {
		webWidget: {
			contactForm: {
				suppress: true,
			},
		},
	})
	ze("messenger", "logoutUser")
	ze("messenger", "hide")
}
